/* eslint-disable @typescript-eslint/no-explicit-any */
import { browser } from '$app/environment';
import type { SlotSize } from '$lib/types/ads';

import { setupTamBids } from '$lib/utils/ads';
import Logger from '$lib/utils/logger';
import { AdPaths, type AdUnitDimension } from './config';

const googletag: any = typeof window !== 'undefined' && browser ? window.googletag : {};
googletag.cmd = googletag?.cmd || [];

const SECONDS_TO_WAIT_AFTER_VIEWABILITY = 30;
const OUTBRAIN_AD_PLACEMENTS = ['home-mrec-btf', 'article-mrec-article_end'];
const REFRESH_KEY = 'refresh';
const REFRESH_VALUE = 'true';

export function formatAdUnit(adUnit: string): string {
  let adPath = AdPaths.MREC_ATF;
  switch (true) {
    case adUnit.includes('mrec') && adUnit.includes('atf'):
      adPath = AdPaths.MREC_ATF;
      break;
    case adUnit.includes('banner') && adUnit.includes('atf'):
      adPath = AdPaths.BANNER_ATF;
      break;
    case adUnit.includes('mrec') || adUnit.includes('viewall_feed'):
      adPath = AdPaths.MREC_INFEED;
      break;
    case adUnit.includes('sticky'):
      adPath = AdPaths.BANNER_STICKY;
      break;
    case adUnit.includes('banner') && (adUnit.includes('feeds') || adUnit.includes('detailsfeeds')):
      adPath = AdPaths.BANNER_INFEED;
      break;
    default:
      break;
  }
  return `${adPath}/${adUnit}`;
}

export function defineAndConfigureSlot(
  adUnitName: string,
  adUnitDimension: AdUnitDimension,
  adUnitId: string
) {
  return googletag
    .defineSlot(adUnitName, adUnitDimension, adUnitId)
    .setTargeting(REFRESH_KEY, REFRESH_VALUE)
    .setTargeting('sizes', JSON.stringify(adUnitDimension))
    .addService(googletag.pubads());
}

// test slot is in viewport
export function isSlotVisible(id: string) {
  const element = document.getElementById(id);

  if (!element) {
    Logger.warn(`Element with id #${id} not found.`);
    return false;
  }

  const rect = element.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export function onImpressionViewable(
  event: any,
  targetSlot: any
  // adUnitName: string,
  // placementName: string,
): void {
  // sendAnalyticsEvent(EventNames.AD_IMPRESSION, {
  //   ad_unit: adUnitName,
  //   placement_name: placementName
  // });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const slot = (event as any).slot;
  const currentSlotID = slot?.getSlotElementId();
  const currentAdunit = slot?.getAdUnitPath();
  const currentSize = slot?.getSizes().map((item: SlotSize) => [item.width, item.height]);

  if (
    slot.getTargeting(REFRESH_KEY).indexOf(REFRESH_VALUE) > -1 &&
    isSlotVisible(currentSlotID) &&
    slot === targetSlot &&
    !OUTBRAIN_AD_PLACEMENTS.includes(currentAdunit)
  ) {
    setTimeout(function () {
      if (slot !== undefined && isSlotVisible(currentSlotID)) {
        slot && setupTamBids(currentSlotID, currentAdunit, currentSize, slot);
        // googletag.pubads().refresh([slot]);
      }
    }, SECONDS_TO_WAIT_AFTER_VIEWABILITY * 1000);
  }
}

export function initializeAdServices(): void {
  googletag.pubads().enableSingleRequest();
  googletag.pubads().disableInitialLoad();
  googletag.enableServices();
}

export function setupAdIntersectionObserver(adUnitId: string): void {
  const observerOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '500px',
    threshold: 0.5
  };

  const observerCallback: IntersectionObserverCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        if (window.PWT_LOADED) {
          const adSlotId = entry.target.id;
          const slot = googletag
            .pubads()
            .getSlots()
            .find((slot: any) => slot?.getSlotElementId() === adSlotId);

          if (slot) {
            const adUnitPath = slot?.getAdUnitPath();
            // Assuming the sizes targeting is a JSON string of number pairs
            const sizesStr = slot?.getTargeting('sizes')[0];
            const sizes: number[][] = JSON.parse(sizesStr);
            setupTamBids(adSlotId, adUnitPath, sizes, slot);
          }

          observer.unobserve(entry.target);
        }
      }
    });
  };

  const adObserver = new IntersectionObserver(observerCallback, observerOptions);

  googletag.cmd.push(() => {
    const adElement = document.getElementById(adUnitId);
    if (adElement) {
      adObserver.observe(adElement);
    }
  });
}
export function setPrivacySettingsAndrefreshAds(childDirectedTreatment: boolean): void {
  Logger.log('Refreshing ads');
  if (browser) {
    googletag.cmd.push(() => {
      googletag.pubads().setPrivacySettings({
        childDirectedTreatment: childDirectedTreatment
      });
      googletag.pubads().refresh();
    });
  }
}
